import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Save, Users } from 'lucide-react'
import { Modal } from '../../../componentesCompartilhados/ui/Modal'
import { FormField } from '../../../componentesCompartilhados/form'
import { TimeFormData } from '../tipos/time'
import { useCreateTime, useUpdateTime } from '../hooks/useTime'
import { zRequiredString } from '../../../utils/validacao'

const timeSchema = z.object({
  descricao: zRequiredString,
  externo: z.boolean().default(false),
})

interface FormularioTimeProps {
  isOpen: boolean
  onClose: () => void
  time?: TimeFormData | null
  isEditing?: boolean
}

export function FormularioTime({ isOpen, onClose, time, isEditing = false }: FormularioTimeProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TimeFormData>({
    resolver: zodResolver(timeSchema),
    defaultValues: { id: undefined, descricao: '', externo: false },
  })

  const createMutation = useCreateTime()
  const updateMutation = useUpdateTime()

  useEffect(() => {
    if (time) {
      reset({ id: time.id, descricao: time.descricao || '', externo: time.externo ?? false })
    } else {
      reset({ id: undefined, descricao: '', externo: false })
    }
  }, [time, reset, isOpen])

  const onSubmit = async (data: TimeFormData) => {
    if (isEditing && data.id) {
      await updateMutation.mutateAsync({ id: data.id, data })
    } else {
      await createMutation.mutateAsync(data)
    }
    onClose()
    reset()
  }

  const footer = (
    <div className="flex justify-end gap-3 px-6 py-4">
      <button
        type="button"
        onClick={() => { onClose(); reset() }}
        className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium"
      >
        Cancelar
      </button>
      <button
        type="submit"
        form="time-form"
        disabled={isSubmitting || createMutation.isLoading || updateMutation.isLoading}
        className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Save className="w-4 h-4 mr-2 inline" />
        {isEditing ? 'Atualizar' : 'Salvar'}
      </button>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { onClose(); reset() }}
      title={isEditing ? 'Editar time' : 'Novo time'}
      subtitle={isEditing ? 'Atualize a descrição do time' : 'Preencha os dados para criar um novo time'}
      icon={
        <div className="w-10 h-10 bg-brand-100 text-brand-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
          <Users className="w-5 h-5" />
        </div>
      }
      size="lg"
      footer={footer}
    >
      <form id="time-form" className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <FormField
          label="Descrição"
          error={errors.descricao}
          labelClassName="block text-sm font-medium text-gray-700 mb-1"
        >
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
            placeholder="Ex: Time de Desenvolvimento"
            {...register('descricao')}
          />
        </FormField>

        <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer hover:bg-gray-50 transition-colors">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
            {...register('externo')}
          />
          <span className="text-sm">
            <span className="block font-medium text-gray-700">Time externo</span>
            <span className="block text-gray-500">
              Pessoas deste time não aparecem em nenhum combo/tela do sistema, apenas nas rotinas
              de manutenção de Pessoas, Usuários, Times e nas rotinas de Bônus Presencial.
            </span>
          </span>
        </label>
      </form>
    </Modal>
  )
}
